/* eslint-disable react/self-closing-comp */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Person } from '@enview/interface/types/Person';
import { RichTextNode, UserAction } from '@enview/interface/types/actions/UserAction';
import { BillDetailed, BillVersion } from '@enview/interface/types/bills/Bill';
import { lookforMentions } from '@enview/interface/utils/RichTextAnnotation';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import { Descendant } from 'slate';
import { UserActionAPI } from '../../../../api';
import { errorHasData } from '../../../../api/base';
import { g as abilityGlossary } from '../../../../config/ability';
import { checkPermission, getSelectedTeamIds } from '../../../../dux';
import { mobileMediaQuery } from '../../../../helpers/mediaQueries';
import ConfirmActionPopover from '../../Popovers/ConfirmActionPopover';
import TeamSelector from '../../TeamSelector';
import GateTooltip, { GateTooltipFeature } from '../../Tooltips/TooltipGate';
import './CommentForm.scss';
import RichTextEditor from './RichTextEditor';

export const sourceFeature = {
  BILL_ANNOTATION: 'bill-annotation',
  PERSON_NOTE: 'person-note',
  TEAM_DISCUSSION: 'team-discussion',
};
const PLACEHOLDER_TEXT = '<i class="placeholder-text">Add something</i>';
const EMPTY_COMMENT_TEXT =
  'To post a comment, please type some text into the Comment field, upload a file, or both.';
const FILE_TOO_LARGE_TEXT = 'File exceeds the maximum file size of 200MB.';
const FAIL_TO_ADD_COMMENT =
  'An error occurred, please try again. If this message persists, contact support in the lower right hand corner.';
const POST_COMMENT_POPOVER_TITLE = 'Select the teams you want to share with:';
const WARNING_CLASS = 'alert alert-warning warning';
const DANGER_CLASS = 'alert alert-danger warning';

interface CommentFormProps {
  aiSummary?: boolean;
  billVersionDisplayed?: BillVersion;
  bill?: BillDetailed;
  person?: Person;
  userActionDraft?: Partial<UserAction>;
  onSaveCallback?: (userAction: Partial<UserAction>) => void;
}

const CommentForm = (props: CommentFormProps) => {
  const node: any = useRef();
  const { billVersionDisplayed, aiSummary, userActionDraft, onSaveCallback } = props;

  const selectedTeams = useSelector(getSelectedTeamIds);
  const [comment, setComment] = useState(PLACEHOLDER_TEXT as any);
  const [mention, setMention] = useState<number[]>([]);
  const [warningMessage, setWarningMessage] = useState(EMPTY_COMMENT_TEXT);
  const [errorClass, setErrorClass] = useState(WARNING_CLASS);
  const [selectedFile, setSelectedFile] = useState(null);
  const [displayValidationWarning, setDisplayValidationWarning] = useState(false);
  const [displayPostPopup, setDisplayPostPopup] = useState(false);
  const [reset, setReset] = useState(false);
  const dispatch = useDispatch();

  const canComment = () => {
    return dispatch(
      checkPermission(abilityGlossary.CREATE, abilityGlossary.BILL_ANNOTATION, true),
    );
  };

  const [addUserAction, { isError, error }] =
    UserActionAPI.endpoints.addUserAction.useMutation();

  useEffect(() => {
    setDisplayValidationWarning(false);
    if (isError) {
      setErrorClass(DANGER_CLASS);
      setDisplayValidationWarning(true);
      if (
        error &&
        errorHasData(error) &&
        error.data.errorType === 'billAnnotationJurisdictionCheck'
      ) {
        setWarningMessage(error.data.message);
      } else {
        setWarningMessage(FAIL_TO_ADD_COMMENT);
      }
    }
  }, [isError]);

  const selectUserFile = (event: any) => {
    setDisplayValidationWarning(false);
    const file = event.target.files[0];
    if (file && file.size > 200000000) {
      setWarningMessage(FILE_TOO_LARGE_TEXT);
      setDisplayValidationWarning(true);
      return;
    }
    setSelectedFile(file);
  };

  const onPostClicked = () => {
    // user unable to comment
    if (!canComment()) {
      return;
    }
    setDisplayValidationWarning(false);
    setErrorClass(WARNING_CLASS);
    // no comment exists yet
    if ((!comment || comment === PLACEHOLDER_TEXT) && !selectedFile) {
      setComment(PLACEHOLDER_TEXT);
      setWarningMessage(EMPTY_COMMENT_TEXT);
      setDisplayValidationWarning(true);
    } else {
      // check for mentions
      if (typeof comment === 'object') {
        let mentions: number[] = [];
        comment.forEach((el: RichTextNode) => {
          mentions = mentions.concat(lookforMentions(el));
        });
        setMention(mentions);
      }
      setDisplayPostPopup(true);
    }
  };

  const onSave = (): void => {
    const { bill, person } = props;
    const data: Partial<UserAction> = {
      billData: bill
        ? {
            billId: bill.id,
            billVersionId: billVersionDisplayed ? billVersionDisplayed.id : undefined,
          }
        : undefined,
      person,
      teams: selectedTeams,
      sourceFeature: sourceFeature.BILL_ANNOTATION,
      mentionUserIds: mention.length > 0 ? mention : undefined,
    };
    // if only comment was added.
    if (comment && comment !== PLACEHOLDER_TEXT) {
      // TODO: clean up nested if statements after migrating comment data to `commentJson` field
      if (typeof comment === 'object') {
        data.commentJson = { children: comment };
        data.comment = '';
      } else {
        let sanitizedComment: string = comment.replace(/<div>/g, ' ');
        sanitizedComment = sanitizeHtml(sanitizedComment, { allowedTags: [] });
        data.comment = sanitizedComment;
      }
    }
    // Save Comment if there is a comment entity to save.
    addUserAction({ userAction: data, attachedFile: selectedFile });
    setComment(PLACEHOLDER_TEXT);
    setSelectedFile(null);
    setDisplayPostPopup(false);
    setReset(true);

    // If a callback was provided by client code, call it
    if (onSaveCallback) {
      onSaveCallback(data);
    }
  };

  const onChangeToEditor = (event: Descendant[]) => {
    setComment(event);
  };

  const postButton = (
    <ConfirmActionPopover
      align="end"
      closePopup={() => setDisplayPostPopup(false)}
      content={
        <div className="post-confirmation">
          <p className="popover-confirm-body">{POST_COMMENT_POPOVER_TITLE}</p>
          {mention.length > 0 ? <TeamSelector mention={true} /> : <TeamSelector />}
          <div className="text-center mt-3">
            <button
              className="btn btn-sm btn-block btn-action"
              onClick={onSave}
              type="button"
            >
              Post
            </button>
          </div>
        </div>
      }
      disableReposition={mobileMediaQuery.matches}
      positions={['bottom']}
      show={displayPostPopup}
      target={
        <div className="post-button">
          <button
            className="btn-action"
            disabled={!canComment()}
            onClick={onPostClicked}
            type="button"
          >
            Post
          </button>
        </div>
      }
      toggleCallback={() => {}}
    />
  );

  const fileSelectedPrompt = (file: any) => {
    if (file) {
      return <div className="alert alert-info text-center">{file.name} selected</div>;
    }
    return null;
  };

  return (
    <>
      {displayValidationWarning && <div className={errorClass}>{warningMessage}</div>}
      <>{fileSelectedPrompt(selectedFile)}</>

      <div className="rich-text-editor" id="text-editor" ref={node}>
        <RichTextEditor
          aiSummary={aiSummary}
          billId={billVersionDisplayed?.billId}
          billVersionId={billVersionDisplayed?.id}
          disabled={!canComment()}
          onChangeToEditor={onChangeToEditor}
          reset={reset}
          selectUserFile={selectUserFile}
          setReset={setReset}
          userAction={userActionDraft}
        />
        <div className="post-button-row d-flex flex-row">{postButton}</div>
      </div>
      {!canComment() && (
        <GateTooltip
          accountType="pro"
          anchorId="text-editor"
          customContent={
            <Trans
              components={{
                editorLink: <a href={t('urls.annotatingBills')} target="_blank" />,
                gate: <p className="tooltip-header" />,
              }}
              i18nKey="featureGating.tooltipText.richTextEditor"
              key="featureGating.tooltipText.richTextEditor"
            />
          }
          featureName={GateTooltipFeature.CommentForm}
          place="bottom"
        />
      )}
    </>
  );
};

export default CommentForm;
